import {
  useState,
  useRef,
  useCallback,
  useEffect,
  type ReactNode,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

export interface ListEditorItem {
  id: string;
}

export interface ListEditorProps<T extends ListEditorItem> {
  items: readonly T[];
  onChange: (next: readonly T[]) => void;
  renderItem: (
    item: T,
    isEditing: boolean,
    onItemChange: (patch: Partial<T>) => void,
  ) => ReactNode;
  createItem: () => T;
  addLabel?: string;
  maxItems?: number;
  disabled?: boolean;
  label?: string;
}

export function ListEditor<T extends ListEditorItem>({
  items,
  onChange,
  renderItem,
  createItem,
  addLabel = "+ Добавить",
  maxItems,
  disabled = false,
  label,
}: ListEditorProps<T>) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (!editingId) return;
    const handleMouseDown = (e: MouseEvent) => {
      const rowEl = rowRefs.current.get(editingId);
      if (rowEl && !rowEl.contains(e.target as Node)) {
        setEditingId(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editingId]);

  const handlePencilClick = useCallback((id: string) => {
    setEditingId((prev: string | null) => (prev === id ? null : id));
  }, []);

  const handleDone = useCallback(() => {
    setEditingId(null);
  }, []);

  const handleDelete = useCallback(
    (id: string) => {
      onChange(items.filter((item: T) => item.id !== id));
      setEditingId((prev: string | null) => (prev === id ? null : prev));
    },
    [items, onChange],
  );

  const handleAdd = useCallback(() => {
    const newItem = createItem();
    onChange([...items, newItem]);
    setEditingId(newItem.id);
  }, [createItem, items, onChange]);

  const handleItemChange = useCallback(
    (id: string, patch: Partial<T>) => {
      onChange(
        items.map((item: T) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [items, onChange],
  );

  const handleMouseEnter = useCallback((id: string) => {
    setHoveredId(id);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
  }, []);

  const handleRowKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>, id: string) => {
      if (e.key === "Escape" && editingId === id) {
        e.preventDefault();
        setEditingId(null);
      }
    },
    [editingId],
  );

  const handlePencilKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>, id: string) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handlePencilClick(id);
      }
    },
    [handlePencilClick],
  );

  const handleDoneKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleDone();
      } else if (e.key === "Escape") {
        e.preventDefault();
        handleDone();
      }
    },
    [handleDone],
  );

  const rootStyle: CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const addDisabled = maxItems !== undefined && items.length >= maxItems;
  const listLabel = label ?? "List";

  return (
    <div
      style={rootStyle}
      role="group"
      aria-label={listLabel}
      aria-disabled={disabled || undefined}
    >
      {label && (
        <div
          id={"list-editor-label-" + label.replace(/\s+/g, "-")}
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: "6px",
          }}
        >
          {label}
        </div>
      )}
      <div
        role="list"
        aria-label={listLabel + " items"}
        style={{ display: "flex", flexDirection: "column" }}
      >
        {items.map((item: T, index: number) => {
          const isEditing = editingId === item.id;
          const isHovered = hoveredId === item.id;
          const itemPosition = index + 1;
          return (
            <div
              key={item.id}
              ref={(el: HTMLDivElement | null) => {
                if (el) rowRefs.current.set(item.id, el);
                else rowRefs.current.delete(item.id);
              }}
              role="listitem"
              aria-label={listLabel + " item " + itemPosition}
              aria-posinset={itemPosition}
              aria-setsize={items.length}
              onMouseEnter={() => handleMouseEnter(item.id)}
              onMouseLeave={handleMouseLeave}
              onKeyDown={(e: ReactKeyboardEvent<HTMLDivElement>) =>
                handleRowKeyDown(e, item.id)
              }
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                padding: "8px",
                borderBottom: "1px solid var(--paper-rule)",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                {renderItem(item, isEditing, (patch: Partial<T>) =>
                  handleItemChange(item.id, patch),
                )}
              </div>
              {isEditing ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleDone}
                    onKeyDown={handleDoneKeyDown}
                    aria-label={"Finish editing " + listLabel + " item " + itemPosition}
                    tabIndex={0}
                    style={{
                      font: "500 11px 'JetBrains Mono', monospace",
                      letterSpacing: ".08em",
                      padding: "2px 8px",
                      border: "1px solid var(--paper-rule)",
                      background: "var(--paper-bg)",
                      color: "var(--paper-ink)",
                      cursor: "pointer",
                    }}
                  >
                    Готово
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.id)}
                    aria-label={"Delete " + listLabel + " item " + itemPosition}
                    tabIndex={0}
                    style={{
                      width: "22px",
                      height: "22px",
                      border: "none",
                      background: "transparent",
                      color: "var(--paper-ink-2)",
                      cursor: "pointer",
                      font: "400 16px sans-serif",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                      flexShrink: 0,
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => handlePencilClick(item.id)}
                  onKeyDown={(e: ReactKeyboardEvent<HTMLButtonElement>) =>
                    handlePencilKeyDown(e, item.id)
                  }
                  aria-label={"Edit " + listLabel + " item " + itemPosition}
                  tabIndex={0}
                  style={{
                    width: "22px",
                    height: "22px",
                    border: "none",
                    background: "transparent",
                    color: "var(--paper-ink-2)",
                    cursor: "pointer",
                    font: "400 16px sans-serif",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    flexShrink: 0,
                    visibility: isHovered ? "visible" : "hidden",
                  }}
                >
                  ✎
                </button>
              )}
            </div>
          );
        })}
      </div>
      <button
        type="button"
        onClick={handleAdd}
        disabled={addDisabled}
        aria-label={addLabel + " to " + listLabel}
        aria-disabled={addDisabled || undefined}
        tabIndex={0}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px",
          font: "500 11px 'JetBrains Mono', monospace",
          letterSpacing: ".08em",
          color: "var(--paper-ink-2)",
          cursor: addDisabled ? "not-allowed" : "pointer",
          border: "1px dashed var(--paper-rule)",
          background: "transparent",
          width: "100%",
          opacity: addDisabled ? 0.5 : 1,
        }}
      >
        {addLabel}
      </button>
    </div>
  );
}
